import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDarkMode } from "../../contexts/DarkModeContext";
import { useNavigate } from "react-router-dom";
import "./AllStudents.css";
import { BsPlusCircleFill } from "react-icons/bs";
import { FaFileAlt, FaPlus } from "react-icons/fa";
import { getTeacherClasses, getTeacherStudentsByClass } from "../../redux/slices/lessonsSlice";

function AllStudents() {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { teacherClasses, teacherStudents, lessonsLoading, studentsLoading, studentsPagination } = useSelector((state) => state.lessons);
  const { currentUser } = useSelector((state) => state.auth);
  
  const [activeTab, setActiveTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isGuestMode, setIsGuestMode] = useState(true);

  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isTeacher = userRole === "teacher";

  // التحقق من وجود token عند تحميل المكون
  useEffect(() => {
    const token = localStorage.getItem("token3almny");
    if (token) {
      setIsGuestMode(false);
      if (isTeacher) {
        dispatch(getTeacherClasses());
      }
    } else {
      setIsGuestMode(true);
    }
  }, [dispatch, isTeacher]);

  // جلب الطلاب عند تغيير الفصل النشط
  useEffect(() => {
    if (!isGuestMode && isTeacher && teacherClasses.length > 0) {
      const selectedClass = teacherClasses[activeTab];
      if (selectedClass?.id) {
        console.log("جلب طلاب الفصل:", selectedClass.id);
        dispatch(getTeacherStudentsByClass({ 
          page: currentPage 
        }));
      }
    }
  }, [activeTab, currentPage, teacherClasses, isGuestMode, isTeacher, dispatch]);

  // Mapping للأيقونات حسب اسم الفصل
  const getClassIcon = (className) => {
    const iconMap = {
      "الرياضيات": "/math.png",
      "الدراسات الاجتماعية": "/drasat.png",
      "اللغة العربية": "/arabic.png",
      "العلوم": "/science.png",
      "جغرافيا": "/gohraphia.png",
      "فيزياء": "/physic.png",
      "اللغة الإنجليزية": "/english.png",
      "تاريخ": "/tarekh.png",
      "اللغة الانجليزية": "/english.png",
    };
    return iconMap[className] || "/science.png";
  };

  const handleTabClick = (index) => {
    setActiveTab(index);
    setCurrentPage(1); // Reset to first page when changing tab
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleCreateReport = (studentId, classId) => {
    navigate(`/create-report/${studentId}/${classId}`);
  };

  const handleViewReports = (studentId, classId) => {
    navigate(`/student-reports/${studentId}/${classId}`);
  };

  if (!isTeacher) {
    return (
      <div className="text-center py-5">
        <h3>غير مسموح لك بالوصول لهذه الصفحة</h3>
        <p>هذه الصفحة متاحة للمعلمين فقط</p>
      </div>
    );
  }

  if (lessonsLoading || studentsLoading) {
    return (
      <div className="all-students-page">
        <div className="container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`all-students-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="container">
        {/* Breadcrumb */}
        <div className="lessons-breadcrumb">
          <h1 className="breadcrumb-title">الطلاب</h1>
          <div className="breadcrumb-links2">
            <span className="breadcrumb-link">الرئيسية</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">الطلاب</span>
          </div>
        </div>

        {/* Class Tabs */}
        {!isGuestMode && teacherClasses.length > 0 && (
          <div className="subjects-tabs mt-3">
            <div className="tabs-container">
              {teacherClasses.map((classItem, index) => (
                <button
                  key={classItem.id}
                  className={`subject-tab ${activeTab === index ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                >
                  <img 
                    src={getClassIcon(classItem.name)} 
                    alt={classItem.name} 
                    className="tab-icon" 
                  />
                  <span>{classItem.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Students Grid */}
        <div className="students-grid">
          {teacherStudents.length > 0 ? (
            teacherStudents.map((student, index) => (
              <div key={student.id || index} className="student-card">
                <div className="student-image">
                  <img 
                    src={student.prof_img || student.image || "https://randomuser.me/api/portraits/men/32.jpg"} 
                    alt={student.name} 
                    onError={(e) => {
                      e.target.src = "https://randomuser.me/api/portraits/men/32.jpg";
                    }}
                  />
                  <BsPlusCircleFill className="plus-icon" />
                </div>
                <h5 className="student-name mb-3" style={{fontSize : "15px"}}>{student.name}</h5>
                <div className="student-info">
                  <span className="student-class">{student.class_name}</span>
                  <span className="student-subject">{student.subject_name}</span>
                </div>
                
                {/* Report Actions */}
                <div className="student-actions">
                  <button
                    className="action-btn create-report-btn"
                    onClick={() => handleCreateReport(student.id, teacherClasses[activeTab]?.id)}
                    title="إضافة تقرير"
                  >
                    <FaPlus />
                    إضافة تقرير
                  </button>
                  
                  <button
                    className="action-btn view-reports-btn"
                    onClick={() => handleViewReports(student.id, teacherClasses[activeTab]?.id)}
                    title="عرض التقارير"
                  >
                    <FaFileAlt />
                    عرض التقارير
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center w-100">
              <p>لا توجد بيانات للطلاب في هذا الفصل</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {studentsPagination && studentsPagination.pages_count >= 1 && (
          <div className="pagination-container mt-4">
            <div className="pagination">
              {Array.from({ length: studentsPagination.pages_count }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllStudents;
